import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Brand } from "../components/Brand";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Key, ShieldCheck, Smartphone } from "lucide-react";

export default function Login() {
  const { login, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [unlocked, setUnlocked] = useState(false);

  // Flow 2FA / must_change_password
  const [step, setStep] = useState("credentials"); // credentials | totp | enroll_totp | change_password
  const [totpCode, setTotpCode] = useState("");
  const [enrollData, setEnrollData] = useState(null); // {secret, qr_svg, uri}
  const [newPwd, setNewPwd] = useState("");
  const [newPwd2, setNewPwd2] = useState("");

  const afterLoginSuccess = async (res) => {
    toast.success("Accesso effettuato");
    // Setup pendente → pagina dedicata (cambio password + 2FA)
    if (res?.must_change_password || (res?.two_factor_required && !res?.two_factor_enabled)) {
      nav("/setup", { replace: true });
      return;
    }
    nav("/admin");
  };

  const submitCredentials = async (e) => {
    e.preventDefault();
    try {
      const res = await login(email, password);
      if (res.step === "totp_required") {
        setStep("totp");
        return;
      }
      await afterLoginSuccess(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login fallito");
    }
  };

  const submitTotp = async (e) => {
    e.preventDefault();
    try {
      const res = await login(email, password, totpCode);
      if (res.step === "ok") {
        await afterLoginSuccess(res);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Codice 2FA non valido");
    }
  };

  const confirmEnrollment = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/totp/confirm", { code: totpCode });
      toast.success("2FA attivato!");
      setTotpCode("");
      // Nuovo login completo: ora deve passare TOTP + eventuale must_change_password
      const res = await login(email, password, "");
      if (res.step === "totp_required") {
        setStep("totp");
      } else {
        await afterLoginSuccess(res);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Conferma fallita");
    }
  };

  const submitPwdChange = async (e) => {
    e.preventDefault();
    if (newPwd !== newPwd2) return toast.error("Le password non coincidono");
    if (newPwd.length < 12) return toast.error("Minimo 12 caratteri");
    try {
      await api.post("/auth/change-password", { old_password: password, new_password: newPwd });
      toast.success("Password aggiornata — accedi di nuovo");
      // Logout e torna a credentials
      try { await api.post("/auth/logout"); } catch {}
      setPassword(""); setNewPwd(""); setNewPwd2("");
      setStep("credentials");
      setTotpCode("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cambio password fallito");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 overflow-hidden" data-testid="login-page">
      {/* LEFT — Ferrari ignition */}
      <div className="hidden lg:flex relative overflow-hidden bg-black items-center justify-center">
        <img src="https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1600&q=80" alt="" className={`absolute inset-0 w-full h-full object-cover transition-all duration-1000 ${unlocked ? "scale-110 opacity-70" : "scale-100 opacity-40"}`} />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        <div className="absolute inset-0 grid-bg opacity-30" />
        {unlocked && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: [0, 0.8, 0.5] }} transition={{ duration: 1.2 }}
              className="absolute top-1/2 -left-20 w-[80%] h-2 bg-gradient-to-r from-transparent via-white to-transparent blur-md" />
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: [0, 0.6, 0.3] }} transition={{ duration: 1.2, delay: 0.1 }}
              className="absolute top-1/2 mt-12 -left-20 w-[80%] h-2 bg-gradient-to-r from-transparent via-[#ff5577] to-transparent blur-md" />
          </>
        )}
        <div className="relative z-10 text-center">
          <motion.button onClick={() => setUnlocked(!unlocked)} whileTap={{ scale: 0.95 }} data-testid="ignition-key" className="relative group">
            <div className={`w-44 h-44 rounded-full grid place-items-center transition-all duration-700 ${unlocked ? "bg-[#ff0033]/30 border-2 border-[#ff0033] shadow-[0_0_80px_rgba(255,0,51,0.7)]" : "bg-white/[0.03] border-2 border-white/15"}`}>
              <motion.div animate={{ rotate: unlocked ? 90 : 0 }} transition={{ type: "spring", stiffness: 150, damping: 15 }}>
                <Key size={64} className={unlocked ? "text-[#ff0033]" : "text-white/40"} strokeWidth={1.4} />
              </motion.div>
            </div>
            {unlocked && (
              <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                className="absolute -inset-4 rounded-full border-2 border-[#ff0033]/40 animate-ping" />
            )}
          </motion.button>
          <motion.div animate={{ opacity: unlocked ? 1 : 0.4 }} className="mt-8">
            <div className="font-display text-4xl font-black tracking-tighter uppercase">
              {unlocked ? <span className="neon-text">Motore Acceso</span> : "Inserisci la chiave"}
            </div>
            <div className="text-xs uppercase tracking-[0.4em] text-white/40 mt-3">
              {unlocked ? "Procedi con le credenziali" : "Click per accendere"}
            </div>
          </motion.div>
        </div>
      </div>

      {/* RIGHT — credentials / 2FA / enroll / change pwd */}
      <div className="flex items-center justify-center p-8 bg-[#050505] relative">
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm relative z-10">
          <Link to="/"><Brand /></Link>

          {step === "credentials" && (
            <>
              <h1 className="font-display text-3xl font-extrabold uppercase mt-10 tracking-tight">Accedi al Gestionale</h1>
              <p className="text-sm text-white/45 mt-2">Bentornato. Inserisci le tue credenziali.</p>
              <form onSubmit={submitCredentials} className="mt-8 space-y-4">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-white/45">Email</label>
                  <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    className="mt-2 w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#ff0033]/60 focus:outline-none" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-white/45">Password</label>
                  <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                    className="mt-2 w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#ff0033]/60 focus:outline-none" />
                </div>
                <button data-testid="login-submit" disabled={loading} className="btn-neon w-full py-3.5 rounded-xl text-sm">
                  {loading ? "Accesso..." : "Entra"}
                </button>
              </form>
              <div className="my-6 flex items-center gap-3 text-[10px] uppercase tracking-widest text-white/30">
                <span className="flex-1 h-px bg-white/10" /> oppure <span className="flex-1 h-px bg-white/10" />
              </div>
              <button type="button" data-testid="login-google-btn"
                onClick={() => {
                  const redirectUrl = window.location.origin + '/dashboard';
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="w-full py-3.5 rounded-xl text-sm bg-white text-black font-medium flex items-center justify-center gap-3 hover:bg-white/90">
                <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continua con Google
              </button>
              <div className="mt-6 text-xs text-white/40 text-center">
                Accesso riservato agli operatori autorizzati.
              </div>
            </>
          )}

          {step === "totp" && (
            <>
              <div className="flex items-center gap-2 mt-10">
                <ShieldCheck size={22} className="text-[#ff0033]" />
                <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight">Codice 2FA</h1>
              </div>
              <p className="text-sm text-white/45 mt-2">Apri l'app Authenticator (Google / Microsoft / Authy) e inserisci il codice a 6 cifre.</p>
              <form onSubmit={submitTotp} className="mt-8 space-y-4">
                <input data-testid="login-totp" inputMode="numeric" pattern="\d{6}" maxLength={6} autoFocus
                  value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0,6))} required
                  className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-4 text-center text-2xl tracking-[0.5em] font-mono focus:border-[#ff0033]/60 focus:outline-none" />
                <button data-testid="login-totp-submit" disabled={loading} className="btn-neon w-full py-3.5 rounded-xl text-sm">
                  {loading ? "Verifica..." : "Conferma"}
                </button>
                <button type="button" onClick={() => { setStep("credentials"); setTotpCode(""); }} className="w-full text-xs text-white/40 hover:text-white/70 py-2">
                  ← Torna indietro
                </button>
              </form>
            </>
          )}

          {step === "enroll_totp" && enrollData && (
            <>
              <div className="flex items-center gap-2 mt-10">
                <Smartphone size={22} className="text-[#ff0033]" />
                <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight">Attiva 2FA</h1>
              </div>
              <p className="text-sm text-white/45 mt-2">Il tuo ruolo richiede l'autenticazione a 2 fattori. Scansiona il QR con Google Authenticator / Authy e inserisci il codice.</p>
              <div className="mt-6 p-4 rounded-xl bg-white border border-white/10 flex justify-center" data-testid="totp-qr-container">
                <div dangerouslySetInnerHTML={{ __html: enrollData.qr_svg || "" }} />
              </div>
              <div className="mt-3 text-[10px] text-white/40 text-center break-all font-mono" data-testid="totp-secret">Secret: {enrollData.secret}</div>
              <form onSubmit={confirmEnrollment} className="mt-6 space-y-4">
                <input data-testid="totp-enroll-code" inputMode="numeric" pattern="\d{6}" maxLength={6} autoFocus placeholder="Codice a 6 cifre"
                  value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0,6))} required
                  className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-4 text-center text-2xl tracking-[0.5em] font-mono focus:border-[#ff0033]/60 focus:outline-none" />
                <button data-testid="totp-enroll-submit" disabled={loading} className="btn-neon w-full py-3.5 rounded-xl text-sm">
                  Attiva 2FA
                </button>
              </form>
            </>
          )}

          {step === "change_password" && (
            <>
              <div className="flex items-center gap-2 mt-10">
                <Key size={22} className="text-[#ff0033]" />
                <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight">Nuova Password</h1>
              </div>
              <p className="text-sm text-white/45 mt-2">Devi scegliere una nuova password (min. 12 caratteri, 1 maiuscola, 1 minuscola, 1 cifra).</p>
              <form onSubmit={submitPwdChange} className="mt-8 space-y-4">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-white/45">Nuova password</label>
                  <input data-testid="new-password" type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} required minLength={12}
                    className="mt-2 w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#ff0033]/60 focus:outline-none" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-white/45">Conferma password</label>
                  <input data-testid="new-password-2" type="password" value={newPwd2} onChange={(e) => setNewPwd2(e.target.value)} required minLength={12}
                    className="mt-2 w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#ff0033]/60 focus:outline-none" />
                </div>
                <button data-testid="change-password-submit" disabled={loading} className="btn-neon w-full py-3.5 rounded-xl text-sm">
                  Imposta e accedi
                </button>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
